import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  FormControl,
  Button,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { AttachFile, Edit, Delete, CloudUpload, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FormContainer from "../../../../../../components/Forms/FormContainer";
import useDecodedUser from "../../../../../../services/hooks/useDecodedUser";
import { ExecutePostWithParams, GetDataFromApiWithParams, UpdateAndGetResponse } from "../../../../../../services/api/ExecuteApiRequests";
import IPDCFormHeader from "../../../../../../components/Forms/IPDCFormHeader";
import GeneralInfoTab from "./GeneralInfoTab";
import PermitInfoTab from "./PermitInfoTab";
import CommitmentTab from "./CommitmentTab";

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Initial data for registration
const initialData = {
  value: 0, // Step state
  permitNo: "",
  requestDate: new Date().toISOString().slice(0, 10),
  requestType: "",
  parcelWithUPIN: {},
  agreementId: "",
  consultant: "",
  contractor: "",
  startingDate: "",
  finishingDate: "",
  numberOfBuildings: "",
  residenceCount: 0,
  commercialCount: 0,
  shedCount: 0,
  otherCount: 0,
  modificationDetails: "",
  requestFormAttachment: null,
  architecturalPlan: null,
  structuralPlan: null,
  sanitaryPlan: null,
  electricalPlan: null,
  electroMechanicalPlan: null,
  environmentalPlan: null,
  otherPlan: null,
  otherPlanDetails: "",
  ownerCommitment: null,
  consultantCommitment: null,
  contractorCommitment: null,
  description: "",
};

// Steps for registration stepper
const steps = [
  {
    label: "General Information",
    component: (props) => <GeneralInfoTab {...props} />
  },
  {
    label: "Permit Information",
    component: (props) => <PermitInfoTab {...props} />
  },
  {
    label: "Commitments & Description",
    component: (props) => <CommitmentTab {...props} />
  },
];

// Initial edit form data
const initialEditData = {
  id: "",
  permit_no: "",
  requested_on: "",
  request_type: "",
  agreement_id: "",
  consultant_id: "",
  contractor_id: "",
  expected_starting_date: "",
  expected_finishing_date: "",
  no_of_residence_bldgs: 0,
  no_of_commercial_bldgs: 0,
  no_of_sheds: 0,
  no_of_other_bldgs: 0,
  modification_details: "",
  description: "",
  request_registered_by: "",
  permit_issued_by: "",
  issued_on: "",
  area: "",
  park_city: "",
  park_name: "",
  status: "",
  permit_registered_on: "",
  other_bldg_description: "",
  permit_registered_by: "",
  consultant_commitment_path: null,
  contractor_commitment_path: null,
  owner_commitment_path: null,
  plan_architectural_path: null,
  plan_structural_path: null,
  plan_sanitary_path: null,
  plan_electrical_path: null,
  plan_electro_mechanical_path: null,
  plan_environmental_path: null,
  plan_other_path: null,
  request_form: null,
  existing_files: {},
  // File upload states
  requestFormAttachment: null,
  architecturalPlan: null,
  structuralPlan: null,
  sanitaryPlan: null,
  electricalPlan: null,
  electroMechanicalPlan: null,
  environmentalPlan: null,
  otherPlan: null,
  ownerCommitment: null,
  consultantCommitment: null,
  contractorCommitment: null,
  // File names for display
  requestFormName: "",
  architecturalPlanName: "",
  structuralPlanName: "",
  sanitaryPlanName: "",
  electricalPlanName: "",
  electroMechanicalPlanName: "",
  environmentalPlanName: "",
  otherPlanName: "",
  ownerCommitmentName: "",
  consultantCommitmentName: "",
  contractorCommitmentName: "",
};

export default function RegisterBPRequest() {
  const decodedUser = useDecodedUser();
  const [tabValue, setTabValue] = useState(0);
  
  // Registration form states
  const [formData, setFormData] = useState(initialData);
  const [responseError, setResponseError] = useState("");
  const [responseSuccess, setResponseSuccess] = useState("");
  const [agreements, setAgreements] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [contractors, setContractors] = useState([]);
  
  // Edit form states - using a single state object
  const [buildingPermits, setBuildingPermits] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [editFormData, setEditFormData] = useState(initialEditData);
  
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [fetchSuccess, setFetchSuccess] = useState("");

  // Fetch dropdown data
  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Fetch building permits for edit tab
  useEffect(() => {
    if (tabValue === 1) {
      fetchBuildingPermits();
    }
  }, [tabValue]);

  const fetchDropdownData = async () => {
    try {
      setLoading(true);
      
      // Fetch agreements
      const agreementsData = await GetDataFromApiWithParams("agreements");
      setAgreements(agreementsData || []);
      
      // Fetch consultants
      const consultantsData = await GetDataFromApiWithParams("consultants");
      setConsultants(consultantsData || []);
      
      // Fetch contractors
      const contractorsData = await GetDataFromApiWithParams("contractors");
      setContractors(contractorsData || []);
      
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Error loading form data", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildingPermits = async () => {
    try {
      setLoading(true);
      
      const resultData = await GetDataFromApiWithParams("buildingPermits");
      console.log("Building permits fetched:", resultData);
      setBuildingPermits(Array.isArray(resultData) ? resultData : []);
    } catch (error) {
      console.log(error);
      setBuildingPermits([]);
      toast.error("Error fetching building permits: " + (error.message || "Unknown error"), { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setEditMode(false);
    setSelectedPermit(null);
    setEditFormData(initialEditData);
    setEditError("");
    setEditSuccess("");
  };

  // Registration navigation
  const handleNext = () => {
    setFormData({ ...formData, value: formData?.value + 1 });
  };

  const handleBack = () => {
    setFormData({ ...formData, value: formData?.value - 1 });
  };

  // Prepare props for registration step component
  const getStepProps = () => {
    return {
      requestDate: formData?.requestDate,
      setRequestDate: (value) => setFormData({ ...formData, requestDate: value }),
      requestType: formData?.requestType,
      setRequestType: (value) => setFormData({ ...formData, requestType: value }),
      agreementId: formData?.agreementId,
      setAgreementId: (value) => setFormData({ ...formData, agreementId: value }),
      consultant: formData?.consultant,
      setConsultant: (value) => setFormData({ ...formData, consultant: value }),
      contractor: formData?.contractor,
      setContractor: (value) => setFormData({ ...formData, contractor: value }),
      startingDate: formData?.startingDate,
      setStartingDate: (value) => setFormData({ ...formData, startingDate: value }),
      finishingDate: formData?.finishingDate,
      setFinishingDate: (value) => setFormData({ ...formData, finishingDate: value }),
      residenceCount: formData?.residenceCount,
      setResidenceCount: (value) => setFormData({ ...formData, residenceCount: value }),
      commercialCount: formData?.commercialCount,
      setCommercialCount: (value) => setFormData({ ...formData, commercialCount: value }),
      shedCount: formData?.shedCount,
      setShedCount: (value) => setFormData({ ...formData, shedCount: value }),
      otherCount: formData?.otherCount,
      setOtherCount: (value) => setFormData({ ...formData, otherCount: value }),
      modificationDetails: formData?.modificationDetails,
      setModificationDetails: (value) => setFormData({ ...formData, modificationDetails: value }),
      requestFormAttachment: formData?.requestFormAttachment,
      setRequestFormAttachment: (value) => setFormData({ ...formData, requestFormAttachment: value }),
      architecturalPlan: formData?.architecturalPlan,
      setArchitecturalPlan: (value) => setFormData({ ...formData, architecturalPlan: value }),
      structuralPlan: formData?.structuralPlan,
      setStructuralPlan: (value) => setFormData({ ...formData, structuralPlan: value }),
      sanitaryPlan: formData?.sanitaryPlan,
      setSanitaryPlan: (value) => setFormData({ ...formData, sanitaryPlan: value }),
      electricalPlan: formData?.electricalPlan,
      setElectricalPlan: (value) => setFormData({ ...formData, electricalPlan: value }),
      electroMechanicalPlan: formData?.electroMechanicalPlan,
      setElectroMechanicalPlan: (value) => setFormData({ ...formData, electroMechanicalPlan: value }),
      environmentalPlan: formData?.environmentalPlan,
      setEnvironmentalPlan: (value) => setFormData({ ...formData, environmentalPlan: value }),
      otherPlan: formData?.otherPlan,
      setOtherPlan: (value) => setFormData({ ...formData, otherPlan: value }),
      otherPlanDetails: formData?.otherPlanDetails,
      setOtherPlanDetails: (value) => setFormData({ ...formData, otherPlanDetails: value }),
      ownerCommitment: formData?.ownerCommitment,
      setOwnerCommitment: (value) => setFormData({ ...formData, ownerCommitment: value }),
      consultantCommitment: formData?.consultantCommitment,
      setConsultantCommitment: (value) => setFormData({ ...formData, consultantCommitment: value }),
      contractorCommitment: formData?.contractorCommitment,
      setContractorCommitment: (value) => setFormData({ ...formData, contractorCommitment: value }),
      description: formData?.description,
      setDescription: (value) => setFormData({ ...formData, description: value }),
      agreements: agreements,
      consultants: consultants,
      contractors: contractors,
    };
  };

  // Registration form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setResponseError("");
    setResponseSuccess("");
    setSubmitting(true);

    try {
      const data = {
        permit_no: formData?.permitNo,
        requested_on: formData?.requestDate,
        request_type: formData?.requestType,
        agreement_id: formData?.agreementId,
        consultant_id: formData?.consultant?.id,
        contractor_id: formData?.contractor?.id,
        expected_starting_date: formData?.startingDate,
        expected_finishing_date: formData?.finishingDate,
        no_of_residence_bldgs: formData?.residenceCount,
        no_of_commercial_bldgs: formData?.commercialCount,
        no_of_sheds: formData?.shedCount,
        no_of_other_bldgs: formData?.otherCount,
        other_bldg_description: formData?.modificationDetails,
        modification_details: formData?.modificationDetails,
        permit_registered_on: formData?.requestDate,
        request_registered_by: decodedUser?.full_name,
        permit_issued_by: "",
        issued_on: "",
        description: formData?.description,
      };

      const formDataToSubmit = new FormData();
      formDataToSubmit.append("data", JSON.stringify(data));

      // Append files including the request form attachment
      const fileFields = [
        { key: 'requestFormAttachment', fieldName: 'requestForm' },
        { key: 'architecturalPlan', fieldName: 'architectural' },
        { key: 'structuralPlan', fieldName: 'structural' },
        { key: 'sanitaryPlan', fieldName: 'sanitary' },
        { key: 'electricalPlan', fieldName: 'electrical' },
        { key: 'electroMechanicalPlan', fieldName: 'electroMechanical' },
        { key: 'environmentalPlan', fieldName: 'environmental' },
        { key: 'otherPlan', fieldName: 'other' },
        { key: 'ownerCommitment', fieldName: 'ownerCommitment' },
        { key: 'consultantCommitment', fieldName: 'consultantCommitment' },
        { key: 'contractorCommitment', fieldName: 'contractorCommitment' }
      ];

      fileFields.forEach(({ key, fieldName }) => {
        if (formData?.[key]) {
          console.log(`Adding ${key}:`, formData[key].name);
          formDataToSubmit.append(fieldName, formData[key]);
        }
      });

      const responseData = await ExecutePostWithParams(`buildingPermits`, formDataToSubmit, true);
      console.log(responseData);
      
      setResponseSuccess("Building permit submitted successfully!");
      toast.success("Building permit submitted successfully!", { position: "top-right", autoClose: 5000 });
      
      // Reset form
      setFormData(initialData);
      
      // Refresh list if on edit tab
      if (tabValue === 1) {
        fetchBuildingPermits();
      }
      
    } catch (error) {
      console.error("Submit error:", error);
      setResponseError(error?.message || "Failed to submit building permit. Please try again.");
      toast.error(error?.message || "Failed to submit building permit. Please try again.", { position: "top-right", autoClose: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "";
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    return dateString.substring(0, 10);
  };

  // Get filename from path
  const getFileNameFromPath = (path) => {
    if (!path) return "";
    return path.split('/').pop() || path;
  };

  // Handle edit button click - stores ALL permit data
  const handleEditClick = (permit) => {
    console.log("========== EDIT CLICKED ==========");
    console.log("Full permit object:", permit);
    console.log("Request type from API:", permit.request_type);
    console.log("Agreement ID from API:", permit.agreement_id);
    
    
    setSelectedPermit(permit);
    
    // Log all file paths for debugging
    console.log("File paths from permit:");
    console.log("- request_form:", permit.request_form);
    console.log("- request_form_path:", permit.request_form_path);
    console.log("- plan_archtectural_path:", permit.plan_archtectural_path);
    console.log("- plan_structural_path:", permit.plan_structural_path);
    console.log("- plan_sanitary_path:", permit.plan_sanitary_path);
    console.log("- plan_electrical_path:", permit.plan_electrical_path);
    console.log("- plan_electro_mechanical_path:", permit.plan_electro_mechanical_path);
    console.log("- plan_environmental_path:", permit.plan_environmental_path);
    console.log("- plan_other_path:", permit.plan_other_path);
    console.log("- owner_commitment_path:", permit.owner_commitment_path);
    console.log("- consultant_commitment_path:", permit.consultant_commitment_path);
    console.log("- contractor_commitment_path:", permit.contractor_commitment_path);
    
    // Create existing files info object with correct property names
    const existingFiles = {
      requestForm: permit.request_form_path || permit.request_form || null,
      architectural: permit.plan_archtectural_path || null,
      structural: permit.plan_structural_path || null,
      sanitary: permit.plan_sanitary_path || null,
      electrical: permit.plan_electrical_path || null,
      electroMechanical: permit.plan_electro_mechanical_path || null,
      environmental: permit.plan_environmental_path || null,
      other: permit.plan_other_path || null,
      ownerCommitment: permit.owner_commitment_path || null,
      consultantCommitment: permit.consultant_commitment_path || null,
      contractorCommitment: permit.contractor_commitment_path || null,
    };
  
    // Set all permit data in editFormData
    setEditFormData({
      // Basic info
      id: permit.id || "",
      permit_no: permit.permit_no || "",
      requested_on: permit.requested_on ? formatDate(permit.requested_on) : "",
      request_type: permit.request_type ? String(permit.request_type).trim() : "",
      agreement_id: permit.agreement_id ? String(permit.agreement_id).trim() : "",
      consultant_id: permit.consultant_id || "",
      contractor_id: permit.contractor_id || "",
      
      // Dates
      expected_starting_date: permit.expected_starting_date ? formatDate(permit.expected_starting_date) : "",
      expected_finishing_date: permit.expected_finishing_date ? formatDate(permit.expected_finishing_date) : "",
      issued_on: permit.issued_on ? formatDate(permit.issued_on) : "",
      permit_registered_on: permit.permit_registered_on ? formatDate(permit.permit_registered_on) : "",
      
      // Building counts
      no_of_residence_bldgs: permit.no_of_residence_bldgs || 0,
      no_of_commercial_bldgs: permit.no_of_commercial_bldgs || 0,
      no_of_sheds: permit.no_of_sheds || 0,
      no_of_other_bldgs: permit.no_of_other_bldgs || 0,
      
      // Text fields
      modification_details: permit.modification_details || "",
      other_bldg_description: permit.other_bldg_description || "",
      description: permit.description || "",
      
      // Registration info
      request_registered_by: permit.request_registered_by || "",
      permit_issued_by: permit.permit_issued_by || "",
      permit_registered_by: permit.permit_registered_by || "",
      
      // Location/Area info
      area: permit.area || "",
      park_city: permit.park_city || "",
      park_name: permit.park_name || "",
      
      // Status
      status: permit.status || "",
      
      // File paths - use the correct property names
      request_form_path: permit.request_form_path || permit.request_form || null,
      consultant_commitment_path: permit.consultant_commitment_path || null,
      contractor_commitment_path: permit.contractor_commitment_path || null,
      owner_commitment_path: permit.owner_commitment_path || null,
      plan_architectural_path: permit.plan_architectural_path || null,
      plan_structural_path: permit.plan_structural_path || null,
      plan_sanitary_path: permit.plan_sanitary_path || null,
      plan_electrical_path: permit.plan_electrical_path || null,
      plan_electro_mechanical_path: permit.plan_electro_mechanical_path || null,
      plan_environmental_path: permit.plan_environmental_path || null,
      plan_other_path: permit.plan_other_path || null,
      
      // Existing files info
      existing_files: existingFiles,
      
      // Reset file uploads
      requestFormAttachment: null,
      architecturalPlan: null,
      structuralPlan: null,
      sanitaryPlan: null,
      electricalPlan: null,
      electroMechanicalPlan: null,
      environmentalPlan: null,
      otherPlan: null,
      ownerCommitment: null,
      consultantCommitment: null,
      contractorCommitment: null,
      
      // Set file names for display
      requestFormName: getFileNameFromPath(permit.request_form_path || permit.request_form),
      architecturalPlanName: getFileNameFromPath(permit.plan_architectural_path),
      structuralPlanName: getFileNameFromPath(permit.plan_structural_path),
      sanitaryPlanName: getFileNameFromPath(permit.plan_sanitary_path),
      electricalPlanName: getFileNameFromPath(permit.plan_electrical_path),
      electroMechanicalPlanName: getFileNameFromPath(permit.plan_electro_mechanical_path),
      environmentalPlanName: getFileNameFromPath(permit.plan_environmental_path),
      otherPlanName: getFileNameFromPath(permit.plan_other_path),
      ownerCommitmentName: getFileNameFromPath(permit.owner_commitment_path),
      consultantCommitmentName: getFileNameFromPath(permit.consultant_commitment_path),
      contractorCommitmentName: getFileNameFromPath(permit.contractor_commitment_path),
    });
    
    console.log("Edit form data set with files:", {
      request_form_path: permit.request_form_path || permit.request_form,
      plan_architectural_path: permit.plan_architectural_path,
      plan_structural_path: permit.plan_structural_path,
      owner_commitment_path: permit.owner_commitment_path
    });
    
    setEditMode(true);
  };

  // Handle delete
  const handleDeleteClick = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        setEditError("");
        setEditSuccess("");
        // Add your delete API call here
        // await ExecuteDeleteWithParams(`buildingPermits/${id}`);
        setEditSuccess(`Record ${id} deleted successfully!`);
        toast.success(`Record ${id} deleted successfully!`, { position: "top-right", autoClose: 3000 });
        fetchBuildingPermits();
      } catch (error) {
        console.log(error);
        setEditError("Error deleting record: " + (error.message || "Unknown error"));
        toast.error("Error deleting record: " + (error.message || "Unknown error"), { position: "top-right", autoClose: 5000 });
      }
    }
  };

  // Handle edit form submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (submitting) return;
    
    setEditError("");
    setEditSuccess("");
    
    if (!editFormData.id) {
      setEditError("No record selected for editing");
      toast.error("No record selected for editing", { position: "top-right", autoClose: 5000 });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Prepare the data object (matching the addBuildingPermit structure)
      const data = {
        id: editFormData.id,
        requested_on: editFormData.requested_on,
        request_type: editFormData.request_type,
        agreement_id: editFormData.agreement_id,
        consultant_id: editFormData.consultant_id,
        contractor_id: editFormData.contractor_id,
        expected_starting_date: editFormData.expected_starting_date,
        expected_finishing_date: editFormData.expected_finishing_date,
        no_of_residence_bldgs: editFormData.no_of_residence_bldgs,
        no_of_commercial_bldgs: editFormData.no_of_commercial_bldgs,
        no_of_sheds: editFormData.no_of_sheds,
        no_of_other_bldgs: editFormData.no_of_other_bldgs,
        other_bldg_description: editFormData.modification_details,
        modification_details: editFormData.modification_details,
        description: editFormData.description,
        permit_issued_by: editFormData.permit_issued_by,
        issued_on: editFormData.issued_on,
        status: editFormData.status,
        area: editFormData.area,
        park_city: editFormData.park_city,
        park_name: editFormData.park_name,
      };
      
      console.log("Data to update:", data);
  
      // Create FormData
      const formDataToSubmit = new FormData();
      formDataToSubmit.append("data", JSON.stringify(data));
  
      // Append files if new ones are selected
      const fileFields = [
        { key: 'requestFormAttachment', fieldName: 'requestForm' },
        { key: 'architecturalPlan', fieldName: 'architectural' },
        { key: 'structuralPlan', fieldName: 'structural' },
        { key: 'sanitaryPlan', fieldName: 'sanitary' },
        { key: 'electricalPlan', fieldName: 'electrical' },
        { key: 'electroMechanicalPlan', fieldName: 'electroMechanical' },
        { key: 'environmentalPlan', fieldName: 'environmental' },
        { key: 'otherPlan', fieldName: 'other' },
        { key: 'ownerCommitment', fieldName: 'ownerCommitment' },
        { key: 'consultantCommitment', fieldName: 'consultantCommitment' },
        { key: 'contractorCommitment', fieldName: 'contractorCommitment' }
      ];
  
      fileFields.forEach(({ key, fieldName }) => {
        if (editFormData[key] && editFormData[key] instanceof File) {
          console.log(`Adding file - ${key}:`, editFormData[key].name);
          formDataToSubmit.append(fieldName, editFormData[key]);
        }
      });
  
      // Log FormData contents
      console.log("FormData entries:");
      for (let pair of formDataToSubmit.entries()) {
        if (pair[1] instanceof File) {
          console.log(`  ${pair[0]}: [FILE] ${pair[1].name}`);
        } else {
          console.log(`  ${pair[0]}: ${pair[1]}`);
        }
      }
      
      const endpoint = `buildingPermits/edit/buildingPermit/${editFormData.id}`;
      
      console.log("Endpoint:", endpoint);
      
      const responseData = await ExecutePostWithParams(endpoint, formDataToSubmit, true);
      
      console.log("Update response:", responseData);
      
      if (responseData && responseData.success) {
        toast.success("✅ Building permit updated successfully!", { position: "top-right", autoClose: 3000 });
        
        setTimeout(() => {
          setEditMode(false);
          setSelectedPermit(null);
          setEditFormData(initialEditData);
          fetchBuildingPermits();
          setSubmitting(false);
          
        }, 2000);
      } else if (responseData && responseData.message) {
        setEditError(responseData.message);
        toast.error(responseData.message, { position: "top-right", autoClose: 5000 });
        setSubmitting(false);
      } else {
        setEditError("❌ No response from server");
        toast.error("❌ No response from server", { position: "top-right", autoClose: 5000 });
        setSubmitting(false);
      }
    } catch (error) {
      console.error("Error in handleEditSubmit:", error);
      setEditError(error.message || "Error updating record");
      toast.error(error.message || "Error updating record", { position: "top-right", autoClose: 5000 });
      setSubmitting(false);
    }
  };
  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedPermit(null);
    setEditFormData(initialEditData);
    setEditError("");
    setEditSuccess("");
  };

  // Handle file upload changes
  const handleFileChange = (fieldName, file) => {
    setEditFormData({
      ...editFormData,
      [fieldName]: file,
      [`${fieldName}Name`]: file ? file.name : '' // Store filename for display
    });
  };

  // Handle input changes for edit form
  const handleEditInputChange = (field, value) => {
    setEditFormData({
      ...editFormData,
      [field]: value
    });
  };

  // Render file upload button with check icon
  const renderFileUploadButton = (label, fieldName, existingPath, newFile, fileName) => {
    const hasExistingFile = existingPath && !newFile;
    const hasNewFile = !!newFile;
    const isFileAttached = hasExistingFile || hasNewFile;
    
    return (
      <FormControl fullWidth>
        <Button
          variant="outlined"
          component="label"
          startIcon={isFileAttached ? <CheckCircle /> : <CloudUpload />}
          fullWidth
          sx={{ 
            height: '56px',
            backgroundColor: isFileAttached ? '#e8f5e8' : 'transparent',
            borderColor: isFileAttached ? '#4caf50' : 'rgba(0, 0, 0, 0.23)',
            color: isFileAttached ? '#2e7d32' : 'inherit',
            '&:hover': {
              backgroundColor: isFileAttached ? '#d0ebd0' : 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          {hasNewFile ? `Change ${label}` : hasExistingFile ? `Change ${label}` : `Upload ${label}`}
          <input
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(e) => handleFileChange(fieldName, e.target.files[0])}
            style={{ display: "none" }}
          />
        </Button>
        {hasExistingFile && !hasNewFile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <CheckCircle sx={{ fontSize: '1rem', color: '#2e7d32' }} />
            <Typography variant="caption" sx={{ color: '#2e7d32' }}>
              Current file: {fileName || 'File attached'}
            </Typography>
          </Box>
        )}
        {hasNewFile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <CheckCircle sx={{ fontSize: '1rem', color: '#2e7d32' }} />
            <Typography variant="caption" sx={{ color: '#2e7d32' }}>
              New file selected: {newFile.name}
            </Typography>
          </Box>
        )}
      </FormControl>
    );
  };

  if (loading && tabValue === 1 && !editMode) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading building permits...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '80%', mx: 'auto', mt: 2 }}>
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="building permit tabs">
          <Tab label="Register New Building Permit" />
          <Tab label="Edit Building Permits" />
        </Tabs>
      </Box>
      <Paper elevation={3} sx={{ mt: 2 }}>
        
        {/* Global Messages */}
        {fetchError && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }} onClose={() => setFetchError("")}>
            {fetchError}
          </Alert>
        )}
        
        {fetchSuccess && (
          <Alert severity="success" sx={{ mx: 3, mt: 2 }} onClose={() => setFetchSuccess("")}>
            {fetchSuccess}
          </Alert>
        )}
        
        {/* Tab 1: Registration Form */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ width: '100%' }}>
            {responseError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setResponseError("")}>
                {responseError}
              </Alert>
            )}
            
            {responseSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResponseSuccess("")}>
                {responseSuccess}
              </Alert>
            )}
            
            <Box my={2}>
              <Stepper activeStep={formData?.value}>
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            
            {/* Render the current step component */}
            <Box>
              {steps[formData?.value].component(getStepProps())}
            </Box>
            
            <Box mt={2} display="flex" justifyContent="space-between">
              {formData?.value > 0 && (
               <Button 
               variant="contained" 
               onClick={handleBack}
               sx={{ 
                 backgroundColor: '#e0e0e0', // Light gray
                 color: '#1a1a1a', // Black text for better contrast
                 '&:hover': {
                   backgroundColor: '#d5d5d5', // Slightly darker gray on hover
                 }
               }}
             >
               Back
             </Button>
              )}
              {formData?.value < steps.length - 1 ? (
                <Button 
                variant="contained" 
                color="primary" 
                onClick={handleNext}
                sx={{ 
                  backgroundColor: '#e0e0e0',
                  color: '#1a1a1a', 
                  '&:hover': {
                    backgroundColor: '#d5d5d5',
                  }
                }}
              >
                Next
              </Button>
              ) : (
                <Button 
                  variant="contained" 
                  onClick={handleSubmit}
                  disabled={submitting}
                  sx={{ 
                    backgroundColor: '#e0e0e0',
                    color: '#000000',
                    '&:hover': {
                      backgroundColor: '#d5d5d5',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#f5f5f5',
                      color: '#999999',
                    }
                  }}
                >
                  {submitting ? <CircularProgress size={24} /> : "Submit"}
                </Button>
              )}
            </Box>
          </Box>
        </TabPanel>
        
        {/* Tab 2: Edit Form */}
        <TabPanel value={tabValue} index={1}>
          {!editMode ? (
            // List View
            <Card>
              <CardContent>
                {editError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError("")}>
                    {editError}
                  </Alert>
                )}
                
                {editSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEditSuccess("")}>
                    {editSuccess}
                  </Alert>
                )}
                
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                  <TableContainer sx={{ maxHeight: 650 }}>
                    <Table stickyHeader aria-label="building permits table">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Permit No</TableCell>
                          <TableCell>Request Date</TableCell>
                          <TableCell>Request Type</TableCell>
                          <TableCell>Agreement ID</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <CircularProgress size={30} />
                              <Typography variant="body2" sx={{ mt: 1 }}>Loading...</Typography>
                            </TableCell>
                          </TableRow>
                        ) : buildingPermits.length > 0 ? (
                          buildingPermits.map((permit) => (
                            <TableRow key={permit.id} hover>
                              <TableCell>{permit.id}</TableCell>
                              <TableCell>{permit.permit_no || 'N/A'}</TableCell>
                              <TableCell>
                                {permit.requested_on ? new Date(permit.requested_on).toLocaleDateString() : 'N/A'}
                              </TableCell>
                              <TableCell>{permit.request_type || 'N/A'}</TableCell>
                              <TableCell>{permit.agreement_id || 'N/A'}</TableCell>
                              <TableCell>{permit.description || 'N/A'}</TableCell>
                              <TableCell>
                                <IconButton 
                                  color="primary" 
                                  onClick={() => handleEditClick(permit)}
                                  size="small"
                                  title="Edit"
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton 
                                  color="error" 
                                  onClick={() => handleDeleteClick(permit.id)}
                                  size="small"
                                  title="Delete"
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Typography variant="body1" color="textSecondary">
                                No building permits found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </CardContent>
            </Card>
          ) : (
            // Edit Form
            <Paper key={editFormData.id} sx={{ p: 3, mb: 3, width: '100%', mx: 'auto', mt: 2 }}>
              {editError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError("")}>
                  {editError}
                </Alert>
              )}
              
              {editSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEditSuccess("")}>
                  {editSuccess}
                </Alert>
              )}
              
              {/* Loading Indicator (optional) */}
              {submitting && !editSuccess && !editError && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography variant="body2">Updating building permit...</Typography>
                </Box>
              )}
              
              <form onSubmit={handleEditSubmit}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: '#000000', textAlign: 'center' }}>
                  Edit Building Permit (ID: {editFormData.id})
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Basic Information */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Permit Number"
                      value={editFormData.id || ''}
                      onChange={(e) => handleEditInputChange('id', e.target.value)}
                      fullWidth
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Request Date"
                      type="date"
                      value={editFormData.requested_on || ''}
                      onChange={(e) => handleEditInputChange('requested_on', e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Request Type</InputLabel>
                      <Select
                        value={editFormData.request_type || ''}
                        onChange={(e) => handleEditInputChange('request_type', e.target.value)}
                      >
                        <MenuItem value="New">New</MenuItem>
                        <MenuItem value="Maintenance">Maintenance</MenuItem>
                        <MenuItem value="Extension">Extension</MenuItem>
                        <MenuItem value="Modification">Modification</MenuItem>
                        <MenuItem value="Demolish">Demolish</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Agreement</InputLabel>
                      <Select
                        value={editFormData.agreement_id || ''}
                        onChange={(e) => handleEditInputChange('agreement_id', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Select Agreement</em>
                        </MenuItem>
                        {agreements.map((agreement) => (
                          <MenuItem key={agreement.id} value={agreement.id}>
                            {agreement.agreement_id} - {agreement.company_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Consultant</InputLabel>
                      <Select
                        value={editFormData.consultant_id || ''}
                        onChange={(e) => handleEditInputChange('consultant_id', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Select Consultant</em>
                        </MenuItem>
                        {consultants.map((consultant) => (
                          <MenuItem key={consultant.id} value={consultant.id}>
                            {consultant.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Contractor</InputLabel>
                      <Select
                        value={editFormData.contractor_id || ''}
                        onChange={(e) => handleEditInputChange('contractor_id', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Select Contractor</em>
                        </MenuItem>
                        {contractors.map((contractor) => (
                          <MenuItem key={contractor.id} value={contractor.id}>
                            {contractor.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Dates */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Expected Starting Date"
                      type="date"
                      value={editFormData.expected_starting_date || ''}
                      onChange={(e) => handleEditInputChange('expected_starting_date', e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Expected Finishing Date"
                      type="date"
                      value={editFormData.expected_finishing_date || ''}
                      onChange={(e) => handleEditInputChange('expected_finishing_date', e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  {/* Building Counts */}
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Residence Buildings"
                      type="number"
                      value={editFormData.no_of_residence_bldgs}
                      onChange={(e) => handleEditInputChange('no_of_residence_bldgs', parseInt(e.target.value) || 0)}
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Commercial Buildings"
                      type="number"
                      value={editFormData.no_of_commercial_bldgs}
                      onChange={(e) => handleEditInputChange('no_of_commercial_bldgs', parseInt(e.target.value) || 0)}
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Sheds"
                      type="number"
                      value={editFormData.no_of_sheds}
                      onChange={(e) => handleEditInputChange('no_of_sheds', parseInt(e.target.value) || 0)}
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Other Buildings"
                      type="number"
                      value={editFormData.no_of_other_bldgs}
                      onChange={(e) => handleEditInputChange('no_of_other_bldgs', parseInt(e.target.value) || 0)}
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Modification Details"
                      value={editFormData.modification_details || ''}
                      onChange={(e) => handleEditInputChange('modification_details', e.target.value)}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>

                  {/* Location Info */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Park City"
                      value={editFormData.park_city || ''}
                      onChange={(e) => handleEditInputChange('park_city', e.target.value)}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Park Name"
                      value={editFormData.park_name || ''}
                      onChange={(e) => handleEditInputChange('park_name', e.target.value)}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Area"
                      value={editFormData.area || ''}
                      onChange={(e) => handleEditInputChange('area', e.target.value)}
                      fullWidth
                      type="number"
                    />
                  </Grid>

                  {/* Status */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editFormData.status || ''}
                        onChange={(e) => handleEditInputChange('status', e.target.value)}
                      >
                        <MenuItem value="REQUESTED">Requested</MenuItem>
                        <MenuItem value="APPROVED">Approved</MenuItem>
                        <MenuItem value="REJECTED">Rejected</MenuItem>
                        <MenuItem value="ISSUED">Issued</MenuItem>
                        <MenuItem value="COMPLETED">Completed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Permit Issued By"
                      value={editFormData.permit_issued_by || ''}
                      onChange={(e) => handleEditInputChange('permit_issued_by', e.target.value)}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Issued On"
                      type="date"
                      value={editFormData.issued_on || ''}
                      onChange={(e) => handleEditInputChange('issued_on', e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {console.log("EDIT FORM DATAAAAAAAA",editFormData)}
                  {/* Request Form Attachment */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Request Form
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    {renderFileUploadButton(
                      "Request Form",
                      "requestFormAttachment",
                      editFormData.existing_files?.requestForm,
                      editFormData.requestFormAttachment,
                      editFormData.requestFormName
                    )}
                  </Grid>
                  
                  {/* Plans and Documents */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Plans and Documents
                    </Typography>
                  </Grid>
                  
                  {/* Architectural Plan */}
                  <Grid item xs={12} md={6}>
                    {renderFileUploadButton(
                      "Architectural Plan",
                      "architecturalPlan",
                      editFormData.existing_files?.architectural,
                      editFormData.architecturalPlan,
                      editFormData.architecturalPlanName
                    )}
                  </Grid>
                  
                  {/* Structural Plan */}
                  <Grid item xs={12} md={6}>
                    {renderFileUploadButton(
                      "Structural Plan",
                      "structuralPlan",
                      editFormData.existing_files?.structural,
                      editFormData.structuralPlan,
                      editFormData.structuralPlanName
                    )}
                  </Grid>
                  
                  {/* Sanitary Plan */}
                  <Grid item xs={12} md={6}>
                    {renderFileUploadButton(
                      "Sanitary Plan",
                      "sanitaryPlan",
                      editFormData.existing_files?.sanitary,
                      editFormData.sanitaryPlan,
                      editFormData.sanitaryPlanName
                    )}
                  </Grid>
                  
                  {/* Electrical Plan */}
                  <Grid item xs={12} md={6}>
                    {renderFileUploadButton(
                      "Electrical Plan",
                      "electricalPlan",
                      editFormData.existing_files?.electrical,
                      editFormData.electricalPlan,
                      editFormData.electricalPlanName
                    )}
                  </Grid>
                  
                  {/* Electro-Mechanical Plan */}
                  <Grid item xs={12} md={6}>
                    {renderFileUploadButton(
                      "Electro-Mechanical Plan",
                      "electroMechanicalPlan",
                      editFormData.existing_files?.electroMechanical,
                      editFormData.electroMechanicalPlan,
                      editFormData.electroMechanicalPlanName
                    )}
                  </Grid>
                  
                  {/* Environmental Plan */}
                  <Grid item xs={12} md={6}>
                    {renderFileUploadButton(
                      "Environmental Plan",
                      "environmentalPlan",
                      editFormData.existing_files?.environmental,
                      editFormData.environmentalPlan,
                      editFormData.environmentalPlanName
                    )}
                  </Grid>
                  
                  {/* Other Plan */}
                  <Grid item xs={12} md={6}>
                    {renderFileUploadButton(
                      "Other Plan",
                      "otherPlan",
                      editFormData.existing_files?.other,
                      editFormData.otherPlan,
                      editFormData.otherPlanName
                    )}
                  </Grid>
                  
                  {/* Commitment Documents */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Commitment Documents
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    {renderFileUploadButton(
                      "Owner Commitment",
                      "ownerCommitment",
                      editFormData.existing_files?.ownerCommitment,
                      editFormData.ownerCommitment,
                      editFormData.ownerCommitmentName
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    {renderFileUploadButton(
                      "Consultant Commitment",
                      "consultantCommitment",
                      editFormData.existing_files?.consultantCommitment,
                      editFormData.consultantCommitment,
                      editFormData.consultantCommitmentName
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    {renderFileUploadButton(
                      "Contractor Commitment",
                      "contractorCommitment",
                      editFormData.existing_files?.contractorCommitment,
                      editFormData.contractorCommitment,
                      editFormData.contractorCommitmentName
                    )}
                  </Grid>
                  
                  {/* Description */}
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      value={editFormData.description || ''}
                      onChange={(e) => handleEditInputChange('description', e.target.value)}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Grid>
                  
                  {/* Action Buttons */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                      <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={handleCancelEdit}
                        sx={{ minWidth: '120px' }}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                      <Button 
                          type="submit" 
                          variant="contained" 
                          sx={{ 
                            minWidth: '120px',
                            backgroundColor: '#e0e0e0',
                            color: '#000000',
                            '&:hover': {
                              backgroundColor: '#d5d5d5',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: '#f5f5f5',
                              color: '#999999',
                            }
                          }}
                          disabled={submitting}
                        >
                          {submitting ? <CircularProgress size={24} /> : "Update"}
                        </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
}